import React from 'react';
import { css, cx } from '@emotion/css';

export interface EventPageMainContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Direct image URL for the banner (fallback if bannerImageData not provided) */
  imageUrl?: string;
  /** A full CSS background-image value (e.g. `linear-gradient(...)` or `url(...)`) to force */
  backgroundImage?: string;
  /** Optional banner image data from API: will try /assets/{name}{extension} first, then url */
  bannerImageData?: {
    name?: string;
    extension?: string;
    url?: string;
  };
}

const bannerWrapper = css({
  width: '100%',
  marginBottom: '24px',
  position: 'relative',
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-md)',
  boxShadow: 'var(--shadow-300)',
});

const bannerImage = css({
  width: '100%',
  height: '360px',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  position: 'relative',
  borderRadius: 'var(--spacing-md) var(--spacing-md) 0 0',
  overflow: 'hidden',
});

const bannerPlaceholder = css({
  width: '100%',
  height: '360px',
  position: 'relative',
  borderRadius: 'var(--spacing-md) var(--spacing-md) 0 0',
  overflow: 'hidden',
  backgroundColor: 'rgba(var(--pcrgba-300), 0.25)',
});

const bannerContent = css({
  width: '100%',
  maxWidth: '90rem',
  margin: '0 auto',
  display: 'flex',
  padding: '24px',
  gap: '24px',
});

export const EventPageMainContainer: React.FC<EventPageMainContainerProps> = ({ 
  imageUrl,
  backgroundImage,
  bannerImageData,
  className,
  children,
  ...rest 
}) => {
  const resolveBannerUrl = (): string | undefined => {
    const serverUrl = import.meta.env.VITE_API_BASE_URL || "";
    if (backgroundImage) return backgroundImage; // already a full CSS value

    if (bannerImageData?.name && bannerImageData?.extension) {
      const ext = bannerImageData.extension.startsWith('.')
        ? bannerImageData.extension
        : `.${bannerImageData.extension}`;
      return `${serverUrl}/assets/${bannerImageData.name}${ext}`;
    }

    if (bannerImageData?.url) {
      return bannerImageData.url;
    }

    if (imageUrl) return imageUrl;
    return undefined;
  };

  return (
    <div className={cx(bannerWrapper, className)} {...rest}>
      {(() => {
        const src = resolveBannerUrl();
        if (src) {
          // If backgroundImage prop was provided, it's already a full CSS value
          const cssValue = backgroundImage ? src : `url('${src}')`;
          return (
            <div
              className={bannerImage}
              style={{ backgroundImage: cssValue }}
            />
          );
        }
        return <div className={bannerPlaceholder} aria-label="Sem imagem" />;
      })()}
      <div className={bannerContent}>
        {children}
      </div>
    </div>
  );
};

export default EventPageMainContainer;
